import type { LogSource } from '@/types/logger.types'

import type { ApsTargeting } from './header-bidding/aps/types'
import type { HeaderBiddingAuctionResult, HeaderBiddingInterface } from './header-bidding/prebid/types'

export type AdEventType =
    | 'closed'
    | 'destroyed'
    | 'teardown'
    | 'rewarded'
    | 'empty'
    | 'failed'
    | 'ready'
    | 'request'
    | 'rendered'
    | 'bid_rendered'
    | 'showing'
    | 'viewable'
    | 'timer'
    | 'clicked'
    | 'interaction_suspected'
    | 'pod_counter'
    | 'unexpected_behavior'
    | 'native_no_fill'
    | 'slot_stage'
    // dispatched only when the autoplay probe fails, so the slot loses video demand
    | 'autoplay_check'

export type AdFailureKind = 'empty' | 'error' | 'timeout'

export type AdEventListenerArgs = [
    type: AdEventType,
    callback: (
        event: Event & {
            detail?: {
                secondsLeft?: number
                reason?: string
                adPosition?: number
                totalAds?: number
                kind?: AdFailureKind
                gamResponse?: googletag.ResponseInformation | null
            }
        },
    ) => void,
    options?: AddEventListenerOptions | boolean,
]

export type PageAdType =
    | 'sidebar'
    | 'sidebar_bottom'
    | 'sidebar_fullscreen'
    | 'sidebar_bottom_fullscreen'
    | 'sticky_desktop'
    | 'sticky_mobile_non_game_page'
    | 'sticky_mobile'
    | 'sticky_tablet'
    | 'leaderboard'
    | 'leaderboard_rest'
    // widget page ad slots
    | 'widget_sidebar'
    | 'widget_sidebar_bottom'
    | 'widget_horizontal'
    | 'widget_horizontal_2'
    | 'widget_sticky_mobile'
    | 'widget_sticky_tablet'
    | 'universal_banner_1'
    | 'universal_banner_2'
    | 'universal_banner_short_1'

    // partner ads sdk formats
    | 'vertical_1'
    | 'vertical_2'
    | 'vertical_3'
    | 'horizontal_1'

    // Ad-unit donors for the universal slots in native ads.
    | 'universal_banner_1_mrec'
    | 'universal_banner_1_banner'
    | 'universal_banner_2_mrec'
    | 'universal_banner_2_banner'

export const OutOfPageTypes = ['interstitial', 'interstitial_preroll', 'rewarded', 'interstitial_midroll'] as const

export type OutOfPageAdType = (typeof OutOfPageTypes)[number]

export type FailureBannerType = { kind: 'empty' } | { kind: 'error'; reason: string } | { kind: 'timeout' }

export type VideoAdType = 'video_banner'

export enum AdProviderStatus {
    loading = 'loading',
    online = 'online',
    offline = 'offline',
}

export type AdType = PageAdType | OutOfPageAdType | VideoAdType

export type AdHandlerConfig = Record<AdType, { timeout?: number; initialDelayInMs?: number }>

export type AdUnitConfig = Record<'desktop' | 'mobile', Partial<Record<AdType, string>>>

export enum AdvAction {
    preloadRewarded = 'preloadRewarded',
    preloadInterstitial = 'preloadInterstitial',
    showRewarded = 'showRewarded',
    showInterstitial = 'showInterstitial',
    setBannerAdvSettings = 'setBannerAdvSettings',
    adStateChanged = 'adStateChanged',
    showAdvancedBanners = 'showAdvancedBanners',
    hideAdvancedBanners = 'hideAdvancedBanners',
    checkAdBlock = 'checkAdBlock',
    subscribeToAdStateChanges = 'subscribeToAdStateChanges',
}

export type PageAdSize = 'fluid' | [number, number]

export type PageAdOptions = {
    el: string // id of element where to mount the ad
    sizes: PageAdSize[]
    placement: PageAdType
    refresh?: number // seconds
    nativeBanner?: NativeBannerRequest
}

export type PageVideoAdOptions = {
    el: string
    placement: VideoAdType
}

type SharedAdTargeting = {
    playgama_clid?: string
    event_source?: LogSource
    utm_source?: string
    utm_medium?: string
    utm_campaign?: string
    utm_campaign_id?: string
    utm_assetgroup_id?: string
}

export type PortalAdTargeting = SharedAdTargeting & {
    widget_id?: string
    ab?: string
    l?: string
    exp?: string

    hru?: string
    page?: string
    effectiveType?: string
    cohort?: string
    rankingAudience?: string

    content_category?: string
    provider?: string
    game_id?: string

    pg_ad_disabled?: string
    cacheless_mode?: string
    h5_rewarded_auction?: string
}

export type PartnerTargeting = SharedAdTargeting & {
    event_source: LogSource
    external_id: string
    gameId?: string
    external_game_id?: string
    external_revenue_ref?: string
}

export type AdTargeting = PortalAdTargeting & Partial<PartnerTargeting>

export type AdRequestContext = {
    readonly targeting: {
        readonly ad_placement: AdType
    }
}

export type AdProviderConfig = {
    name?: string
    targeting: AdTargeting
    timeout: number
    auid: string
}

export type HbAdUnitRequestOptions = {
    requestContext?: AdRequestContext
    headerBidding?: HeaderBiddingInterface
}

export type OutOfPageHbAdUnitRequestOptions = HbAdUnitRequestOptions & {
    adRootElementId?: string
}

export type AdProviderRequestOptions = HbAdUnitRequestOptions & {
    pendingAuction?: Promise<HeaderBiddingAuctionResult | undefined>
    pendingApsTargeting?: Promise<ApsTargeting | undefined>
}

export type OutOfPageAdProviderRequestOptions = AdProviderRequestOptions & {
    adRootElementId?: string
}

export type AdProviderRequest = AdProviderRequestOptions & {
    requestContext: AdRequestContext
}

export type HbAdUnitRequest = HbAdUnitRequestOptions & {
    requestContext: AdRequestContext
}

export enum AdPlacementType {
    IN_PAGE = 'IN_PAGE',
    OUT_OF_PAGE = 'OUT_OF_PAGE',
}

type NativeBannerAdaptive = { type: 'anchored' } | { type: 'inline'; maxHeight?: number }

export type NativeBannerVariant = { minViewportWidth?: number; sizes: NativeBannerSize[] }

export type NativeBannerCoordinates = 'page' | 'screen'

export type NativeBannerSize = [number, number]

export type NativeBannerSlot = {
    sizes: NativeBannerSize[]
    tablet?: NativeBannerVariant[]
    adaptive?: NativeBannerAdaptive
    coordinates: NativeBannerCoordinates
    adaptiveSize?: boolean
}

export type NativeBannerRequest = {
    coordinates: NativeBannerCoordinates
    adUnitDonor: PageAdType
    adaptive?: NativeBannerAdaptive
    sizes: NativeBannerSize[]
}
